import React, { useState } from 'react';
import {
  Play, ArrowLeft, Clock, CheckCircle, ChevronDown, ChevronRight,
  Heart, Eye, Edit3, Trash2, FileText, Download, ExternalLink,
} from 'lucide-react';
import ComentariosSection from '../ComentariosSection';
import { getItemThumb, getItemThumbHQ, NIVEL_COLORS } from './academiaUtils';

/**
 * Página do curso (linguagem Netflix): hero do curso no topo, módulos em
 * accordion com aulas (mini-thumb, duração, check de concluída) e seção de
 * materiais de apoio. O backend hoje entrega aulas FLAT (curso → aulas);
 * o accordion já estrutura como módulo único, pronto para múltiplos módulos
 * quando a API ganhar esse nível.
 */
const CourseView = ({
  curso, materiais = [],
  onBack, onOpenLesson, onResume, onOpenMaterial, onLike,
  isSuperAdmin, onEdit, onDelete,
  t, isMobile,
}) => {
  const [moduleOpen, setModuleOpen] = useState(true);
  // Hero usa a thumb em ALTA (maxres); onError cai pra hqdefault
  const thumb = getItemThumbHQ(curso);
  const aulas = curso.aulas || [];
  const progresso = curso.progresso || null;
  const nivelColor = curso.nivel ? (NIVEL_COLORS[curso.nivel] || null) : null;
  const proximaAula = aulas.find(a => !a.user_watched) || null;
  const nivelLabel = curso.nivel ? t(`academia.niveis.${curso.nivel}`) : null;

  // Módulos: estrutura pronta para N módulos; hoje o backend entrega 1 nível
  const modulos = [{
    id: 'mod-1',
    titulo: t('academia.course.module_default') || 'Conteúdo do curso',
    aulas,
  }];

  return (
    <div style={{ animation: 'dp-pageFadeIn 0.3s ease' }}>
      {/* ===== HERO DO CURSO ===== */}
      <div style={{
        position: 'relative', borderRadius: '16px', overflow: 'hidden',
        minHeight: isMobile ? '300px' : '340px',
        marginBottom: '24px',
        background: 'linear-gradient(135deg, var(--dp-bg-card), var(--dp-bg-primary))',
        display: 'flex', alignItems: 'flex-end',
      }}>
        {thumb && (
          <img
            src={thumb} alt=""
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: isMobile ? 'center' : 'right center',
            }}
            onError={(e) => {
              // maxres pode não existir no YouTube: cai pra hqdefault 1x
              const fallback = getItemThumb(curso);
              if (fallback && e.target.src !== fallback) e.target.src = fallback;
              else e.target.style.display = 'none';
            }}
          />
        )}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.6) 45%, rgba(10,10,10,0.15) 100%)',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(0deg, rgba(10,10,10,0.88) 0%, transparent 50%)',
        }} />

        {/* Voltar */}
        <button
          onClick={onBack}
          style={{
            position: 'absolute', top: '16px', left: '16px', zIndex: 3,
            display: 'flex', alignItems: 'center', gap: '6px',
            background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: '999px', padding: '6px 14px', cursor: 'pointer',
            color: '#F5F5F5', fontSize: '12px', fontWeight: 600, fontFamily: 'var(--dp-font)',
          }}
        >
          <ArrowLeft size={14} /> {t('academia.labels.voltar') || 'Voltar'}
        </button>

        {/* Admin */}
        {isSuperAdmin && (
          <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 3, display: 'flex', gap: '6px' }}>
            <button onClick={(e) => onEdit(curso, e)} style={{
              background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: '8px', padding: '7px', cursor: 'pointer', display: 'flex',
            }}>
              <Edit3 size={13} style={{ color: '#F5F5F5' }} />
            </button>
            <button onClick={(e) => onDelete(curso, e)} style={{
              background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: '8px', padding: '7px', cursor: 'pointer', display: 'flex',
            }}>
              <Trash2 size={13} style={{ color: '#F87171' }} />
            </button>
          </div>
        )}

        <div style={{
          position: 'relative', zIndex: 2,
          padding: isMobile ? '18px' : '28px 36px',
          maxWidth: '640px', width: '100%',
        }}>
          {curso.categoria && (
            <span style={{
              display: 'inline-block', marginBottom: '10px',
              padding: '3px 10px', borderRadius: '999px',
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.6px',
              background: 'rgba(13,174,255,0.12)', color: 'var(--dp-blue-500)',
              border: '1px solid rgba(13,174,255,0.3)',
              textTransform: 'uppercase', fontFamily: 'var(--dp-font)',
            }}>
              {curso.categoria}
            </span>
          )}
          <h1 style={{
            margin: '0 0 8px 0', fontSize: isMobile ? '22px' : '30px',
            fontWeight: 800, letterSpacing: '-0.7px', lineHeight: 1.15,
            color: '#F5F5F5', fontFamily: 'var(--dp-font)',
          }}>
            {curso.titulo}
          </h1>
          {curso.descricao && (
            <p style={{
              margin: '0 0 10px 0', fontSize: '13.5px', lineHeight: 1.55,
              color: '#A0A0A0', fontFamily: 'var(--dp-font)',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {curso.descricao}
            </p>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
            marginBottom: '14px', fontSize: '12px', color: '#A0A0A0', fontFamily: 'var(--dp-font)',
          }}>
            {curso.duracao_estimada && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={12} /> {curso.duracao_estimada}
              </span>
            )}
            <span>{aulas.length} {t('academia.labels.aulas')}</span>
            {nivelLabel && (
              <span style={{ color: nivelColor || '#A0A0A0', fontWeight: 600 }}>{nivelLabel}</span>
            )}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Eye size={12} /> {curso.views_count || 0}
            </span>
          </div>

          {progresso && progresso.total > 0 && (
            <div style={{ maxWidth: '360px', marginBottom: '14px' }}>
              <div style={{
                height: '5px', borderRadius: '999px',
                background: 'rgba(255,255,255,0.16)', overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', borderRadius: '999px',
                  width: `${progresso.percentual}%`,
                  background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
                }} />
              </div>
              <p style={{ margin: '6px 0 0 0', fontSize: '11.5px', color: '#A0A0A0', fontFamily: 'var(--dp-font)' }}>
                {progresso.assistidas} {t('academia.labels.de')} {progresso.total} {t('academia.labels.aulas')} · {progresso.percentual}%
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {aulas.length > 0 && (
              <button className="dp-btn-cta dp-btn-cta--sm" onClick={onResume}>
                <Play size={14} fill="#fff" />
                {proximaAula && progresso?.assistidas > 0
                  ? (t('academia.labels.continuar') || 'Continuar')
                  : (t('academia.home.hero_watch') || 'Assistir agora')}
              </button>
            )}
            <button
              onClick={() => onLike(curso)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '9px 16px', borderRadius: '12px',
                background: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: curso.user_liked ? '#EF4444' : '#F5F5F5',
                fontSize: '13px', fontWeight: 600, fontFamily: 'var(--dp-font)', cursor: 'pointer',
              }}
            >
              <Heart size={14} fill={curso.user_liked ? '#EF4444' : 'none'} />
              {curso.likes_count || 0}
            </button>
          </div>
        </div>
      </div>

      {/* ===== CORPO: módulos + materiais ===== */}
      <div style={{
        display: 'flex', gap: '20px',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'flex-start',
      }}>
        {/* Módulos (accordion) */}
        <div style={{ flex: isMobile ? 'none' : '1 1 62%', minWidth: 0, width: '100%' }}>
          {modulos.map((modulo) => (
            <div key={modulo.id} style={{
              background: 'var(--dp-bg-card)', borderRadius: '14px',
              border: '1px solid var(--dp-border-subtle)', overflow: 'hidden',
              marginBottom: '14px',
            }}>
              <button
                onClick={() => setModuleOpen(o => !o)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer',
                }}
              >
                <span style={{
                  fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)',
                  fontFamily: 'var(--dp-font)',
                }}>
                  {modulo.titulo}
                  <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 500, color: 'var(--dp-text-tertiary)' }}>
                    {modulo.aulas.length} {t('academia.labels.aulas')}
                  </span>
                </span>
                <ChevronDown size={16} style={{
                  color: 'var(--dp-text-tertiary)',
                  transform: moduleOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }} />
              </button>

              {moduleOpen && modulo.aulas.map((aula, idx) => {
                const aulaThumb = getItemThumb(aula);
                return (
                  <div
                    key={aula.id}
                    onClick={() => onOpenLesson(aula)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 18px', cursor: 'pointer',
                      borderTop: '1px solid var(--dp-border-subtle)',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-card-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: aula.user_watched ? 'rgba(16,185,129,0.15)' : 'var(--dp-bg-input)',
                      border: aula.user_watched ? '1px solid rgba(52,211,153,0.4)' : '1px solid var(--dp-border-subtle)',
                      fontSize: '10px', fontWeight: 700,
                      color: aula.user_watched ? '#34D399' : 'var(--dp-text-tertiary)',
                      fontFamily: 'var(--dp-font)',
                    }}>
                      {aula.user_watched ? <CheckCircle size={12} /> : (idx + 1)}
                    </span>

                    {/* Mini-thumb da aula */}
                    <div style={{
                      width: '84px', height: '47px', borderRadius: '6px', flexShrink: 0,
                      overflow: 'hidden', background: 'var(--dp-bg-input)', position: 'relative',
                      display: isMobile ? 'none' : 'block',
                    }}>
                      {aulaThumb ? (
                        <img src={aulaThumb} alt="" loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Play size={14} style={{
                          position: 'absolute', top: '50%', left: '50%',
                          transform: 'translate(-50%,-50%)', color: 'var(--dp-text-tertiary)',
                        }} />
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        margin: 0, fontSize: '13px', fontWeight: 600,
                        color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {aula.titulo}
                      </p>
                      {aula.duracao_estimada && (
                        <p style={{
                          margin: '2px 0 0 0', fontSize: '11px', color: 'var(--dp-text-tertiary)',
                          fontFamily: 'var(--dp-font)', display: 'flex', alignItems: 'center', gap: '4px',
                        }}>
                          <Clock size={10} /> {aula.duracao_estimada}
                        </p>
                      )}
                    </div>
                    <ChevronRight size={14} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          ))}

          {/* Descrição completa (o hero clampa em 2 linhas; aqui o texto
              inteiro, com quebras de linha preservadas) */}
          {curso.descricao && (curso.descricao.length > 140 || curso.descricao.includes('\n')) && (
            <div style={{
              background: 'var(--dp-bg-card)', borderRadius: '14px',
              border: '1px solid var(--dp-border-subtle)', padding: '18px',
              color: 'var(--dp-text-secondary)', fontSize: '14px', lineHeight: 1.7,
              whiteSpace: 'pre-wrap', marginBottom: '14px', fontFamily: 'var(--dp-font)',
            }}>
              {curso.descricao}
            </div>
          )}

          {/* Conteúdo rico do curso (legado — campo removido do modal) */}
          {curso.conteudo_rich && (
            <div
              className="academia-rich-content"
              style={{
                background: 'var(--dp-bg-card)', borderRadius: '14px',
                border: '1px solid var(--dp-border-subtle)', padding: '18px',
                color: 'var(--dp-text-secondary)', fontSize: '14px', lineHeight: 1.7,
                whiteSpace: 'pre-wrap', marginBottom: '14px',
              }}
              dangerouslySetInnerHTML={{ __html: curso.conteudo_rich }}
            />
          )}

          {/* Comentários */}
          <ComentariosSection conteudoTipo={curso.tipo} conteudoId={curso.id} />
        </div>

        {/* Materiais de apoio (recursos da mesma categoria) */}
        <div style={{ flex: isMobile ? 'none' : '1 1 38%', minWidth: 0, width: '100%' }}>
          <div style={{
            background: 'var(--dp-bg-card)', borderRadius: '14px',
            border: '1px solid var(--dp-border-subtle)', overflow: 'hidden',
          }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--dp-border-subtle)' }}>
              <span style={{
                fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)',
              }}>
                {t('academia.course.materials') || 'Materiais de apoio'}
              </span>
            </div>
            {materiais.length === 0 ? (
              <p style={{
                padding: '18px', margin: 0, fontSize: '12.5px',
                color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('academia.course.materials_empty') || 'Nenhum material anexado a este curso ainda.'}
              </p>
            ) : materiais.map((mat, idx) => (
              <div
                key={mat.id}
                onClick={() => onOpenMaterial(mat)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '11px 18px', cursor: 'pointer',
                  borderTop: idx > 0 ? '1px solid var(--dp-border-subtle)' : 'none',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-card-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  width: '30px', height: '30px', borderRadius: '8px', flexShrink: 0,
                  background: 'rgba(13,174,255,0.08)', border: '1px solid rgba(13,174,255,0.22)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FileText size={14} style={{ color: 'var(--dp-blue-500)' }} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: '12.5px', fontWeight: 600,
                    color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {mat.titulo}
                  </p>
                  {(mat.tipo_arquivo || mat.tamanho_arquivo) && (
                    <p style={{ margin: '1px 0 0 0', fontSize: '10.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                      {[mat.tipo_arquivo?.toUpperCase(), mat.tamanho_arquivo].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                {mat.resource_type === 'link'
                  ? <ExternalLink size={13} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                  : <Download size={13} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseView;
